/**
 * Login Page
 * Welcome screen with email/password and social auth
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleIcon, AppleIcon, CoffeeIcon } from '../components/icons/CategoryIcons';
import { CoffeeTooltip } from '../components/ui/CoffeeTooltip';
import { authApi } from '../api/transactionApi';
import { useAuthStore } from '../store/authStore';

const BUY_ME_A_COFFEE_URL = 'https://ko-fi.com/nilocoffeetime';

interface LoginPageProps {
  onLogin: () => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const { t } = useTranslation();
  const { login: storeLogin } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comingSoonMessage, setComingSoonMessage] = useState<'google' | 'apple' | 'signup' | null>(null);

  // Handle coming soon message display
  const showComingSoon = useCallback((type: 'google' | 'apple' | 'signup') => {
    setComingSoonMessage(type);
    setTimeout(() => setComingSoonMessage(null), 3000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(email, password);

      // Update Zustand auth store with user info only
      // Token is stored in HttpOnly cookie by backend (XSS protection)
      if (response.success && response.data) {
        storeLogin(response.data.user);
      }

      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 relative">
      {/* Playful Coffee Tooltip - slides in from left */}
      <CoffeeTooltip href={BUY_ME_A_COFFEE_URL} delay={1000} duration={5000} />

      {/* Support the Project - Top Right Corner */}
      <a
        href={BUY_ME_A_COFFEE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 px-3 py-2 rounded-full border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all duration-200 group"
        title="Buy me a coffee"
      >
        <CoffeeIcon className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="hidden sm:inline text-sm font-medium">Buy me a coffee</span>
      </a>

      <div className="w-full max-w-sm">
        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-slate-100 mb-12">
          {t('pages.login.title')}
        </h1>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="seu@email.com"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
              {t('pages.login.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('pages.login.loggingIn') : t('pages.login.login')}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-700" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-slate-950 text-slate-400">
              {t('pages.login.continueWith')}
            </span>
          </div>
        </div>

        {/* Social Login Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <button
              onClick={() => showComingSoon('google')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-100 font-medium transition-colors disabled:opacity-50"
            >
              <GoogleIcon className="w-5 h-5" />
              <span>Google</span>
            </button>
            {comingSoonMessage === 'google' && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 rounded-lg text-sm font-medium shadow-lg z-10 animate-fade-in bg-amber-900/90 text-amber-200 border border-amber-700 whitespace-nowrap">
                {t('pages.login.comingSoon')}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => showComingSoon('apple')}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-100 font-medium transition-colors disabled:opacity-50"
            >
              <AppleIcon className="w-5 h-5" />
              <span>Apple</span>
            </button>
            {comingSoonMessage === 'apple' && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 rounded-lg text-sm font-medium shadow-lg z-10 animate-fade-in bg-amber-900/90 text-amber-200 border border-amber-700 whitespace-nowrap">
                {t('pages.login.comingSoon')}
              </div>
            )}
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500 relative">
          <p>
            {t('pages.login.noAccount')}{' '}
            <button
              onClick={() => showComingSoon('signup')}
              className="text-primary-500 hover:text-primary-400 font-medium"
            >
              {t('pages.login.signUp')}
            </button>
          </p>
          {comingSoonMessage === 'signup' && (
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 rounded-lg text-sm font-medium shadow-lg z-10 animate-fade-in bg-amber-900/90 text-amber-200 border border-amber-700 whitespace-nowrap">
              {t('pages.login.comingSoon')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
